/**
 * L'offre d'un utilisateur, dans sa fiche de back-office (docs/10 §8, docs/16).
 *
 * C'est ici que se fait l'activation manuelle — **la seule façon de vendre en
 * v1**. Le bouton est masqué sans `billing.subscription.manage_any`, ce qui ne
 * protège rien : la route serveur porte la même permission, et c'est elle qui
 * refuse. Le masquage évite seulement de proposer un geste qui échouera.
 *
 * L'écran ne connaît aucun nom de plan en dur : la liste vient de
 * `GET /billing/plans`. Un plan « Teacher Business » ajouté en base apparaîtra
 * dans ce sélecteur sans déploiement, ce qui est exactement la promesse de
 * docs/10 §1.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useActivateSubscription,
  useCancelUserSubscription,
  useChangeUserPlan,
  useUserEntitlements,
} from "@/features/billing/api/useAdminBilling";
import { usePlans } from "@/features/billing/api/useBilling";
import { usePermissions } from "@/features/auth";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";

const MANAGE_ANY = "billing.subscription.manage_any";

export function UserBillingCard({ userId }: { userId: string }) {
  const { t } = useTranslation("billing");
  const { can } = usePermissions();
  const entitlements = useUserEntitlements(userId);
  const plans = usePlans();
  const activate = useActivateSubscription();
  const changePlan = useChangeUserPlan();
  const cancel = useCancelUserSubscription();
  const [selected, setSelected] = useState("");

  if (!can(MANAGE_ANY)) {
    return null;
  }
  if (entitlements.isError) {
    return (
      <Card title={t("admin.title")}>
        <ErrorState error={entitlements.error} />
      </Card>
    );
  }

  const currentPlan = entitlements.data?.plan_code;
  const options = (plans.data ?? []).filter((plan) => !plan.is_default);
  const target = selected !== "" ? selected : (options[0]?.code ?? "");
  // Le compte est déjà sur un plan payant : le geste n'est plus « activer »
  // mais « changer », et l'API refuse une seconde activation par un 409.
  const isSubscribed = currentPlan !== undefined && !isDefaultPlan(plans.data, currentPlan);
  const mutation = isSubscribed ? changePlan : activate;

  return (
    <Card title={t("admin.title")}>
      <p className="text-sm text-slate-600 dark:text-slate-400">
        {t("admin.currentPlan", { plan: planName(plans.data, currentPlan) })}
      </p>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="block text-slate-600 dark:text-slate-400">{t("admin.choosePlan")}</span>
          <select
            className="mt-1 rounded border border-slate-300 px-2 py-1 dark:border-slate-700 dark:bg-slate-900"
            value={target}
            onChange={(event) => {
              setSelected(event.target.value);
            }}
          >
            {options.map((plan) => (
              <option key={plan.id} value={plan.code}>
                {plan.name}
              </option>
            ))}
          </select>
        </label>

        <Button
          onClick={() => {
            mutation.mutate({ userId, planCode: target });
          }}
          disabled={mutation.isPending || target === ""}
        >
          {isSubscribed ? t("admin.changePlan") : t("admin.activate")}
        </Button>

        {isSubscribed ? (
          <Button
            variant="secondary"
            onClick={() => {
              cancel.mutate({ userId });
            }}
            disabled={cancel.isPending}
          >
            {t("admin.cancel")}
          </Button>
        ) : null}
      </div>

      {mutation.isError ? <ErrorState error={mutation.error} /> : null}
      {cancel.isError ? <ErrorState error={cancel.error} /> : null}

      {entitlements.data !== undefined ? (
        <ul className="mt-4 space-y-1 text-sm">
          {entitlements.data.features.map((feature) => (
            <li key={feature.code} className="flex justify-between gap-4">
              <span className="text-slate-600 dark:text-slate-400">{feature.label}</span>
              <span className="font-mono text-xs">
                {/* Affichage brut, back-office : un administrateur qui diagnostique
                    un accès veut voir `null` plutôt que « illimité ». C'est le
                    seul écran où la valeur se lit telle qu'elle est en base. */}
                {JSON.stringify(feature.value)}
                {feature.source !== "plan" ? ` (${feature.source})` : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}

function isDefaultPlan(
  plans: { code: string; is_default: boolean }[] | undefined,
  code: string,
): boolean {
  return plans?.find((plan) => plan.code === code)?.is_default ?? false;
}

function planName(
  plans: { code: string; name: string }[] | undefined,
  code: string | undefined,
): string {
  if (code === undefined) {
    return "…";
  }
  return plans?.find((plan) => plan.code === code)?.name ?? code;
}
